import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import GrainOverlay from "@/components/GrainOverlay";
import BlogTopbar from "@/components/BlogTopbar";
import ShareButton from "@/components/ShareButton";
import useScrollTop from "@/hooks/useScrollTop";
import useGalleryData from "@/hooks/useGalleryData";
import { fetchPost, postShareUrl, resolveImage } from "@/lib/adminApi";
import { fmtDate } from "@/pages/BlogList";

function setMeta(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

// Single blog post — quiet reading page with the post's own meta.
export default function BlogPost() {
  useScrollTop();
  const { slug } = useParams();
  const { gallery } = useGalleryData();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    setP(null); setErr("");
    fetchPost(slug)
      .then((d) => mounted && setP(d))
      .catch((e) => mounted && setErr(e.response?.status === 404 ? "This post could not be found." : "Failed to load the post."));
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (!p) return;
    document.title = p.meta_title || `${p.title} — ${gallery.artist_name}`;
    setMeta("description", p.meta_description || p.excerpt);
  }, [p, gallery.artist_name]);

  const body = useMemo(() => (p ? DOMPurify.sanitize(p.body_html || "", { ADD_ATTR: ["target"] }) : ""), [p]);

  return (
    <div className="grid-root blog-root" data-testid="post-root">
      <GrainOverlay />
      <BlogTopbar />

      {err && (
        <div className="detail-empty" data-testid="post-error">
          <p>{err} <Link to="/blog" className="link-underline">Back to the blog</Link></p>
        </div>
      )}

      {p && (
        <article className="post" data-testid={`post-${p.slug}`}>
          <header className="post__head">
            <Link to="/blog" className="detail-back mono-caps" data-testid="post-back">&larr; All posts</Link>
            {p.tags?.length > 0 && (
              <p className="post__tags mono-caps" data-testid="post-tags">
                {p.tags.map((t) => <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="post__tag">{t}</Link>)}
              </p>
            )}
            <h1 className="post__title" data-testid="post-title">{p.title}</h1>
            <p className="post__meta mono-caps" data-testid="post-meta">
              <time dateTime={p.date}>{fmtDate(p.date)}</time>
              {p.author && <><span className="contact__dot">&middot;</span><span>{p.author}</span></>}
            </p>
          </header>

          {p.image_url && (
            <figure className="post__hero" data-testid="post-hero">
              <img src={resolveImage(p.image_url)} alt="" decoding="async" />
            </figure>
          )}

          <div className="post-body post__body" dangerouslySetInnerHTML={{ __html: body }} data-testid="post-body" />

          <footer className="post__foot">
            <ShareButton url={postShareUrl(p.slug)} title={p.title} className="detail-share" testId="post-share" />
            <Link to="/paintings" className="detail-share mono-caps" data-testid="post-to-paintings">View the paintings</Link>
            <a className="detail-share mono-caps" href={`mailto:${gallery.artist_email}?subject=${encodeURIComponent("Re: " + p.title)}`} data-testid="post-contact">Write to the artist</a>
          </footer>
        </article>
      )}
    </div>
  );
}
